import React, { useState } from 'react';
import { LeadStatus } from '@/types/lead';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getStatusDisplayInfo } from '@/services/leads/leadStatus';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { Clock, ChevronDown, ChevronUp } from 'lucide-react';
import UpdateLeadStatusForm from './UpdateLeadStatusForm';

interface LeadStatusCardProps {
  leadId: string;
  currentStatus: LeadStatus;
  lastUpdated?: string;
  onStatusChange: (status: LeadStatus, notes?: string) => void;
  className?: string;
}

const LeadStatusCard: React.FC<LeadStatusCardProps> = ({ 
  leadId,
  currentStatus, 
  lastUpdated,
  onStatusChange,
  className
}) => {
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [displayStatus, setDisplayStatus] = useState<LeadStatus>(currentStatus);
  const statusInfo = getStatusDisplayInfo(displayStatus);

  const handleStatusUpdated = (newStatus: LeadStatus, notes?: string) => {
    // Update the local display status immediately for better UX
    setDisplayStatus(newStatus);
    
    // Close the form after successful update
    setShowUpdateForm(false);
    
    // Pass both status and notes up
    onStatusChange(newStatus, notes);
  };

  return (
    <Card className={className}>
      <CardHeader className="space-y-1 pb-4">
        <CardTitle className="text-xl font-semibold">Lead Status</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3 md:space-y-4">
          <div className="flex justify-center">
            <Badge className={`${statusInfo.color} text-white px-3 py-1 text-sm`}>
              {statusInfo.label}
            </Badge>
          </div>
          
          {statusInfo.description && (
            <p className="text-xs text-muted-foreground text-center">
              {statusInfo.description}
            </p>
          )}
          
          {lastUpdated && (
            <div className="flex justify-between text-xs md:text-sm items-center">
              <span className="flex items-center text-muted-foreground">
                <Clock className="h-3 w-3 mr-1" />
                Last updated:
              </span>
              <span>{format(new Date(lastUpdated), 'MMM d, yyyy')}</span>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="pt-2 flex justify-center">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setShowUpdateForm(!showUpdateForm)}
          className="w-full"
        >
          {showUpdateForm ? (
            <>
              <ChevronUp className="h-4 w-4 mr-1" />
              Cancel
            </>
          ) : (
            <>
              <ChevronDown className="h-4 w-4 mr-1" />
              Update Status
            </>
          )}
        </Button>
      </CardFooter>
      
      {showUpdateForm && (
        <div className="px-6 pb-6">
          <UpdateLeadStatusForm
            leadId={leadId}
            currentStatus={displayStatus}
            onStatusUpdated={handleStatusUpdated}
          />
        </div>
      )}
    </Card>
  );
};

export default LeadStatusCard;
